import React, { memo } from 'react';
import classNames from 'classcat';
import { Typography, TypographyVariant } from 'components/Typography';
import Button from 'components/Button';
import Heading from 'components/Heading';
import './header.css';

export const HeaderSize = {
  // TODO: remove the small size when the lobby's carousel navigation is updated. The SmallSize is used for Dynamic Lobby View All.
  SM: 'SM',
  MD: 'MD'
} as const;
export type HeaderSize = (typeof HeaderSize)[keyof typeof HeaderSize];

export const HeaderActionType = {
  BUTTON: 'BUTTON',
  LINK: 'LINK'
} as const;
export type HeaderActionType = (typeof HeaderActionType)[keyof typeof HeaderActionType];

const sizeClassNameMap = {
  [HeaderSize.SM]: 'header--sm',
  [HeaderSize.MD]: ''
};

interface HeaderProps {
  size?: HeaderSize;
  title?: string;
  backButtonLink?: string;
  onClose?: () => void;
  actionTitle?: string;
  actionType?: HeaderActionType;
  onActionClick?: () => void;
}

/**
 * Header Component
 *
 * Displays a header that can include a back button, title, and action buttons.
 * This component from the DS is designed to adapt to both mobile and desktop sizes using responsive CSS.
 * The mobile size typically involves more compact elements, while the desktop size offers more space and possibly additional content.
 *
 * Props:
 *   @param {HeaderSize} [size] - Specifies the size of the header, which can affect its styling and spacing.
 *   @param {string} [title] - Optional title text to be displayed in the center of the header bar.
 *   @param {string} [backButtonLink] - URL for the back button navigation. If provided, a back button is displayed.
 *   @param {string} [actionTitle] - Title for the action button. If provided along with `onActionClick`, the action button is displayed based on the `actionType`.
 *   @param {HeaderActionType} [actionType=HeaderActionType.BUTTON] - Specifies the type of action (BUTTON or LINK). The default is 'BUTTON'.
 *   @param {() => void} [onClose] - Optional handler for closing the header bar, typically through a close icon button.
 *   @param {() => void} [onActionClick] - Handler for an action button click event, executed when the action button is clicked.
 */
const Header = ({
  size = HeaderSize.MD,
  title,
  backButtonLink,
  onClose,
  actionTitle,
  onActionClick,
  actionType = HeaderActionType.BUTTON
}: HeaderProps) => (
  <header className={classNames(['header', sizeClassNameMap[size]])}>
    <div className="header__group header__group--prefix">
      {backButtonLink && (
        // TODO: Update button when the design system Contextual actions components are ready
        <Button
          name="headerBackButton"
          icon="/assets/images1/lobby/arrow-left-primary.svg"
          className="button__grey header__iconButton"
          to={backButtonLink}
          tag="a"
        />
      )}
    </div>
    <div className="header__group header__group--content">
      {title && (
        <Typography
          variant={TypographyVariant.BodyMdStrong}
          desktopVariant={TypographyVariant.BodyXlStrong}
        >
          <Heading level={4}>{title}</Heading>
        </Typography>
      )}
    </div>
    <div className="header__group header__group--suffix">
      {actionTitle && onActionClick && (
        <>
          {actionType === HeaderActionType.BUTTON && (
            <Button
              className="header__button"
              buttonText={actionTitle}
              aria-label={actionTitle}
              onClick={onActionClick}
            />
          )}
          {actionType === HeaderActionType.LINK && (
            <Typography
              variant={TypographyVariant.BodySmStrong}
              desktopVariant={TypographyVariant.BodyMdStrong}
            >
              <a aria-label={actionTitle} onClick={onActionClick}>
                {actionTitle}
              </a>
            </Typography>
          )}
        </>
      )}
      {onClose && (
        // TODO: Update button when the design system Contextual actions components are ready
        <Button
          icon="/assets/images1/close-primary.svg"
          className="button__grey header__iconButton"
          aria-label="Close"
          onClick={onClose}
        />
      )}
    </div>
  </header>
);

export default memo(Header);
